import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import {
  pickReplacementWarehouse,
  REPLACEMENT_GATE_ROLE,
} from '@/lib/permissions';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { useAuthStore } from '@/stores/auth-store';
import { AccessDeniedPage } from '@/pages/access-denied';
import { NoWarehouseAccessPage } from '@/pages/no-warehouse-access';
import { PageLoading } from '@/components/ui/spinner';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * The Front sidebar's "Create in WMS" link
 * (`?createReplacement=1`, see pages/sales-orders/lib/useReplacementDeepLink)
 * arrives in whatever warehouse the agent last selected. When that warehouse
 * denies the page but another of theirs allows it — as a Warehouse Manager,
 * which creating a replacement needs — switch there instead of refusing.
 * Nothing else changes warehouse on a denied route.
 *
 * Either way it says so once: which warehouse it switched to, or why the
 * link can't open here. Returns true while the page should hold
 * (PageLoading): until the live permission list has loaded, and then while
 * the switch lands.
 */
function useReplacementLinkWarehouse(denied: boolean): boolean {
  const { pathname, search } = useLocation();
  const { canAccessAt, warehousesWithRole, permissionsSettled } =
    usePermissions();
  const setSelectedWarehouse = useWarehouseStore((s) => s.setSelectedWarehouse);
  const accessibleWarehouses = useWarehouseStore((s) => s.accessibleWarehouses);
  const warehouses = useWarehouseStore((s) => s.warehouses);
  const announced = useRef(false);

  const isReplacementLink =
    denied && new URLSearchParams(search).get('createReplacement') === '1';
  // Decide once on the live permission list — not the bundled fallback, which
  // may not know a custom role's grant. `warehousesWithRole` reads the
  // persisted user's roles, not the store's accessible list: the link opens a
  // fresh tab, where that list stays empty until /users/me returns, while
  // the roles are already there.
  // North Carolina when it qualifies (pickReplacementWarehouse); without
  // codes yet (fresh tab) the first qualifying warehouse — the list page
  // moves a not-in-WMS link on to North Carolina once they are known.
  const target =
    isReplacementLink && permissionsSettled
      ? pickReplacementWarehouse(
          warehousesWithRole(REPLACEMENT_GATE_ROLE).filter((warehouseId) =>
            canAccessAt(warehouseId, pathname),
          ),
          { accessibleWarehouses, warehouses },
        )
      : undefined;

  const decided = isReplacementLink && permissionsSettled;
  useEffect(() => {
    if (!decided || announced.current) return;
    announced.current = true;
    if (target) {
      const name =
        accessibleWarehouses.find((w) => w.warehouseId === target)
          ?.warehouseName ?? warehouses.find((w) => w.id === target)?.name;
      setSelectedWarehouse(target);
      toast.info(
        `Switched to ${name ?? 'a warehouse where you are a Warehouse Manager'} to open the Create in WMS link.`,
      );
    } else {
      toast.error(
        'The Create in WMS link needs Warehouse Manager access in a warehouse that can open sales orders — ask an admin.',
        { duration: 10_000 },
      );
    }
  }, [decided, target, accessibleWarehouses, warehouses, setSelectedWarehouse]);

  return isReplacementLink && (!permissionsSettled || !!target);
}

export function RoleProtectedRoute({ children }: RoleProtectedRouteProps) {
  const { pathname } = useLocation();
  const { canAccess } = usePermissions();
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const accessibleWarehouses = useWarehouseStore((s) => s.accessibleWarehouses);
  const user = useAuthStore((s) => s.user);

  const bypass = !!user?.isAdmin || !!user?.isAuditor;
  const noWarehouses =
    !selectedWarehouseId && accessibleWarehouses.length === 0;
  const denied = !bypass && !noWarehouses && !canAccess(pathname);
  const holdForReplacementLink = useReplacementLinkWarehouse(denied);

  // Admins bypass all checks
  if (user?.isAdmin) return <>{children}</>;

  // Auditors can see everything (read-only enforced at action level)
  if (user?.isAuditor) return <>{children}</>;

  // If no warehouse selected and user has no warehouse assignments
  if (noWarehouses) {
    // Allow ANY_AUTHENTICATED routes (dashboard, notifications, chat, settings)
    if (canAccess(pathname)) return <>{children}</>;
    return <NoWarehouseAccessPage />;
  }

  // Check route permission
  if (denied) {
    return holdForReplacementLink ? <PageLoading /> : <AccessDeniedPage />;
  }

  return <>{children}</>;
}
